import "./help-tooltip.css"
import React, { useRef, ReactNode } from "react"
import { HoverTooltip, HoverTooltipPosition } from "@nova/component-hover-tooltip"
import className from "classnames"

/**
 * Props for the HelpTooltip component.
 */
interface HelpTooltipProps {
    /**
     * The content to be displayed inside the tooltip.
     */
    content: ReactNode | string
    /**
     * The href attribute for the anchor tag.
     */
    href: string
    theme?: "light" | "dark"
    size?: number
}

/**
 * A component that renders a help tooltip.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode|string} props.content - The content to be displayed inside the tooltip.
 * @param {string} props.href - The href attribute for the anchor tag.
 * @example
 * // Example usage of HelpTooltip component
 * <HelpTooltip content="This is a helpful tooltip" href="#help-link" />
 */
export const HelpTooltip = ({ content, href, theme = "dark", size = 22 }: HelpTooltipProps) => {
    const helpTooltipRef = useRef(null)

    return (
        <a
            href={href}
            ref={helpTooltipRef}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline"
        >
            <div
                className={className(
                    `rounded-full flex justify-center items-center cursor-pointer no-underline p-2`,
                    theme === "dark" ? "bg-slate-700 text-white" : "bg-white text-black"
                )}
                style={{ height: size, width: size }}
            >
                <HoverTooltip
                    id="help-tooltip"
                    parentRef={helpTooltipRef}
                    hoverTooltipPosition={HoverTooltipPosition.top}
                    style={{
                        textAlign: "center",
                        color: "black",
                        padding: "8px",
                        fontSize: "12px",
                    }}
                    containerClassName="transform translate-x-[-11px] translate-y-[-8px]"
                    borderColor="#00000026"
                    hoverTimeoutDelay={0}
                    hoverStartDelay={100}
                >
                    {content}
                </HoverTooltip>
                <p>?</p>
            </div>
        </a>
    )
}
